import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import { renderCellExpand } from '../../components/library/DataGrid/renderCellExpand';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Popover,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { PageWrapper } from '../../styledComponents/wrappers';
import { FormatDateString } from '../../utils/helper';
import {
  FulfillmentLifecycleStatus,
  FulfillmentOrderSummary,
  FulfillmentWaveType,
  OrderType,
} from '../../services/fulfillment/types';
import {
  FULFILLMENT_PAGE_SIZE,
  LIFECYCLE_STATUSES,
  ORDER_TYPE_LABELS,
  WAVE_TYPE_LABELS,
} from '../../services/fulfillment/constants';
import FulfillmentStatusChip from './FulfillmentStatusChip';
import { bulkSetOrderStatus, createWave, setOrderStatus } from '../../services/fulfillment/waveApi';

export const FULFILLMENT_ORDERS_ROUTE = '/fulfillment/orders';

type StatusFilter = 'all' | 'new' | 'active' | 'ready_to_pack' | 'exceptions' | 'shipped';

const FulfillmentOrdersList: React.FC = () => {
  const history = useHistory();
  const [orders, setOrders] = useState<FulfillmentOrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: FULFILLMENT_PAGE_SIZE,
  });
  const [statusPopover, setStatusPopover] = useState<{
    anchor: HTMLElement;
    orderId: number;
    currentStatus: FulfillmentLifecycleStatus | null;
  } | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/fulfillment/orders`);
        setOrders((response as AxiosResponse<FulfillmentOrderSummary[]>).data);
      } catch {
        setError('Failed to load fulfillment orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    let result = orders;

    // Apply status filter first
    if (statusFilter === 'active') {
      result = result.filter(o =>
        ['picking', 'picked', 'packing', 'packed'].includes(o.lifecycleStatus ?? ''),
      );
    } else if (statusFilter === 'ready_to_pack') {
      result = result.filter(o => o.lifecycleStatus === 'picked');
    } else if (statusFilter === 'exceptions') {
      result = result.filter(o => o.waveStatus === 'exception');
    } else if (statusFilter === 'shipped') {
      result = result.filter(o => o.lifecycleStatus === 'shipped');
    } else if (statusFilter === 'new') {
      result = result.filter(o => o.lifecycleStatus === 'pending' || !!!o.lifecycleStatus);
    }

    // Then apply search term
    if (!searchTerm.trim()) return result;
    const term = searchTerm.toLowerCase();
    return result.filter(
      o =>
        o.shopifyOrderName.toLowerCase().includes(term) ||
        o.customerName.toLowerCase().includes(term),
    );
  }, [orders, searchTerm, statusFilter]);

  const handleRowClick = (params: GridRowParams) => {
    history.push(`/fulfillment/orders/${(params.row as FulfillmentOrderSummary).shopifyOrderId}`);
  };

  const handleStatusFilterChange = (_: React.SyntheticEvent, value: StatusFilter) => {
    setStatusFilter(value);
    setPaginationModel(m => ({ ...m, page: 0 }));
  };

  const handleStatusButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: FulfillmentOrderSummary,
  ) => {
    e.stopPropagation();
    setStatusPopover({
      anchor: e.currentTarget,
      orderId: row.shopifyOrderId,
      currentStatus: row.lifecycleStatus,
    });
  };

  const handleStatusSelect = async (newStatus: FulfillmentLifecycleStatus) => {
    if (!statusPopover) return;
    const { orderId } = statusPopover;
    setStatusPopover(null);
    setOrders(prev =>
      prev.map(o => (o.shopifyOrderId === orderId ? { ...o, lifecycleStatus: newStatus } : o)),
    );
    try {
      await setOrderStatus(orderId, newStatus);
    } catch (err) {
      const msg =
        err?.response?.status === 409 ? 'Order is in an active wave' : 'Failed to update status';
      setError(msg);
      // revert
      const response = await axios.get(`${API_URL}/fulfillment/orders`);
      setOrders((response as AxiosResponse<FulfillmentOrderSummary[]>).data);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        headerName: 'Order #',
        field: 'shopifyOrderName',
        flex: 0.7,
        renderCell: renderCellExpand,
      },
      {
        headerName: 'Customer',
        field: 'customerName',
        flex: 1,
        renderCell: renderCellExpand,
      },
      {
        headerName: 'Status',
        field: 'lifecycleStatus',
        flex: 1.1,
        sortable: true,
        sortComparator: (
          a: FulfillmentLifecycleStatus | null,
          b: FulfillmentLifecycleStatus | null,
        ) =>
          LIFECYCLE_STATUSES.indexOf(a ?? 'pending') - LIFECYCLE_STATUSES.indexOf(b ?? 'pending'),
        renderCell: (params: GridRenderCellParams<FulfillmentOrderSummary>) => (
          <FulfillmentStatusChip
            lifecycleStatus={params.row.lifecycleStatus}
            waveStatus={params.row.waveStatus}
            waveId={params.row.waveId}
          />
        ),
      },
      {
        headerName: 'Items',
        field: 'lineItemCount',
        flex: 0.4,
        type: 'number',
      },
      {
        headerName: 'Order type',
        field: 'orderType',
        flex: 0.8,
        valueFormatter: ({ value }) => ORDER_TYPE_LABELS[value as OrderType] ?? value,
      },
      {
        headerName: 'Order date',
        field: 'createdAt',
        flex: 0.8,
        valueFormatter: ({ value }) => (value ? FormatDateString(value) : ''),
      },
      {
        headerName: '',
        field: 'actions',
        flex: 0.5,
        sortable: false,
        renderCell: (params: GridRenderCellParams<FulfillmentOrderSummary>) => (
          <Button
            size="small"
            variant="outlined"
            onClick={e => handleStatusButtonClick(e, params.row)}
            sx={{ fontSize: 11, py: 0.25 }}
          >
            Status
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const selectedOrderType = useMemo((): FulfillmentWaveType | null => {
    if (rowSelectionModel.length === 0) return null;
    const selectedRows = (rowSelectionModel as number[])
      .map(id => orders.find(o => o.shopifyOrderId === id))
      .filter(Boolean) as FulfillmentOrderSummary[];
    const knownTypes = new Set(
      selectedRows.map(o => o.orderType).filter((t): t is FulfillmentWaveType => t !== 'unknown'),
    );
    if (knownTypes.size === 0) return 'mixed';
    if (knownTypes.size === 1) return Array.from(knownTypes)[0];
    return null;
  }, [rowSelectionModel, orders]);

  const handleBulkPending = async () => {
    const selectedGids = (rowSelectionModel as number[])
      .map(id => orders.find(o => o.shopifyOrderId === id)?.shopifyOrderGid)
      .filter(Boolean) as string[];
    if (selectedGids.length === 0) return;
    setBulkLoading(true);
    try {
      await bulkSetOrderStatus(selectedGids, 'pending');
      const response = await axios.get(`${API_URL}/fulfillment/orders`);
      setOrders((response as AxiosResponse<FulfillmentOrderSummary[]>).data);
      setRowSelectionModel([]);
    } catch {
      setError('Failed to update order statuses.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCreateWave = async () => {
    if (!selectedOrderType) return;
    const selectedGids = (rowSelectionModel as number[])
      .map(id => orders.find(o => o.shopifyOrderId === id)?.shopifyOrderGid)
      .filter(Boolean) as string[];
    if (selectedGids.length === 0) return;
    setBulkLoading(true);
    try {
      const wave = await createWave(selectedOrderType, selectedGids);
      history.push(`/fulfillment/waves/${wave.waveId}`);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 409
          ? `An active ${WAVE_TYPE_LABELS[selectedOrderType]} wave already exists`
          : err?.response?.data?.message ?? 'Failed to create wave',
      );
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Fulfillment Orders
      </Typography>
      <Tabs
        value={statusFilter}
        onChange={handleStatusFilterChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="All" value="all" />
        <Tab label="New" value="new" />
        <Tab label="Active" value="active" />
        <Tab label="Ready to Pack" value="ready_to_pack" />
        <Tab label="Exceptions" value="exceptions" />
        <Tab label="Shipped" value="shipped" />
      </Tabs>
      {rowSelectionModel.length > 0 && (
        <Box mb={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button variant="contained" onClick={handleBulkPending} disabled={bulkLoading}>
            Set {rowSelectionModel.length} order{rowSelectionModel.length !== 1 ? 's' : ''} to
            Pending
          </Button>
          <Tooltip
            title={selectedOrderType === null ? 'Cannot mix bike and PA&A orders in a wave' : ''}
          >
            <span>
              <Button
                variant="outlined"
                onClick={handleCreateWave}
                disabled={bulkLoading || selectedOrderType === null}
              >
                Create Wave
              </Button>
            </span>
          </Tooltip>
          {selectedOrderType && (
            <Chip label={WAVE_TYPE_LABELS[selectedOrderType]} size="small" variant="outlined" />
          )}
        </Box>
      )}
      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Search by order # or customer"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: 320 }}
        />
      </Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={filteredOrders}
          columns={columns}
          getRowId={row => row.shopifyOrderId}
          onRowClick={handleRowClick}
          autoHeight
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={model => setRowSelectionModel(model)}
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          pageSizeOptions={[
            FULFILLMENT_PAGE_SIZE,
            FULFILLMENT_PAGE_SIZE * 2,
            FULFILLMENT_PAGE_SIZE * 4,
          ]}
          initialState={{
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: FULFILLMENT_PAGE_SIZE } },
          }}
          sx={{ cursor: 'pointer' }}
          rowHeight={56}
        />
      )}
      <Popover
        open={!!statusPopover}
        anchorEl={statusPopover?.anchor}
        onClose={() => setStatusPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {LIFECYCLE_STATUSES.map(s => (
          <MenuItem
            key={s}
            selected={s === statusPopover?.currentStatus}
            onClick={() => handleStatusSelect(s)}
            sx={{ fontSize: 13 }}
          >
            {s}
          </MenuItem>
        ))}
      </Popover>
    </PageWrapper>
  );
};

export default FulfillmentOrdersList;
